import React, {useContext} from 'react';
import classnames from 'classnames';
import {List} from 'immutable';
import {withRouter, WithRouterProps} from 'react-router';
import {pluralize} from 'lib/stringUtils';

import QuestionsList from 'components/PracticeView/QuestionsList/QuestionsList.react';
import QuestionsListHeader from 'components/PracticeView/QuestionsListHeader/QuestionsListHeader.react';
import QuestionsListFooter from 'components/PracticeView/QuestionsList/QuestionsListFooter.react';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import {MobilePracticeViewContext} from 'components/PracticeView/MobilePracticeViewProvider.context';

import {QuestionModelV3 as Question} from 'src/resources/augmented/Question/QuestionModel.v3';
import {QuestionSetModelV1 as QuestionSet} from 'resources/augmented/QuestionSet/QuestionSetModel.v1';

import {getQuestionSetsQuery} from './TeacherAssignmentPracticeView.queries';

interface Props {
  activeQuestion: Question;
  selectedQuestionCount: number;
  makeQuestionSetOnChangeHandler: (questionSet: QuestionSet) => void;
  selectedQuestionSets: List<QuestionSet>;
  isSelectAllQuestionSetsMode: boolean;
  isRearranging: boolean;
  toggleSelectAllQuestionSets: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuestionClick: ({
    question,
    questionSet
  }: {
    question: Question;
    questionSet: QuestionSet;
  }) => () => void;
  isLoading: boolean;
  scrollToQuestionById: React.RefObject<any>;
  paginatedQuestionsListManager: PaginatedResultsManager;
  questionSets: List<QuestionSet>;
}

export const FolderPracticeViewQuestionList = ({
  activeQuestion,
  selectedQuestionCount,
  makeQuestionSetOnChangeHandler,
  selectedQuestionSets,
  isSelectAllQuestionSetsMode,
  isRearranging,
  toggleSelectAllQuestionSets,
  onQuestionClick,
  isLoading,
  scrollToQuestionById,
  paginatedQuestionsListManager,
  questionSets
}: Props) => {
  const {isQuestionsListInView, setIsQuestionsListInView, isMobile} =
    useContext(MobilePracticeViewContext);

  // taken directly from SPV
  const isQuestionSetSelected = (questionSet) => {
    const isIncluded = selectedQuestionSets.includes(questionSet.getId());
    return isSelectAllQuestionSetsMode ? !isIncluded : isIncluded;
  };

  const makeMobileOnQuestionClick = ({question, questionSet}) => {
    return () => {
      if (isMobile) {
        // Only checking if its mobile because component would need to be rerendered to detect changes
        // it's less resource-intensive to assume that if this is being fired, the questions list is in view
        // and always dismiss it as false.
        setIsQuestionsListInView(false);
      }
      onQuestionClick({question, questionSet})();
    };
  };

  // ### START PROPS ###
  const questionsListProps = {
    scrollToQuestionById,
    onScrollLastQuestionIntoView: async () => {
      if (!paginatedQuestionsListManager.isRequestInFlight) {
        await paginatedQuestionsListManager.fetchNextPage();
      }
    },
    scrollToTop: false,
    header: (
      <TeacherAssignmentQuestionsListHeader
        isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
        selectedQuestionCount={selectedQuestionCount}
        isRearranging={isRearranging}
        toggleSelectAllQuestionSets={toggleSelectAllQuestionSets}
        questionSetCount={questionSets.size}
      />
    ),
    footer: (
      <QuestionsListFooter
        activeQuestion={activeQuestion}
        setActiveQuestionState={(question, questionSet) => {
          const onQuestionClickFunction = onQuestionClick({question, questionSet});
          onQuestionClickFunction();
        }}
        scrollToQuestionById={scrollToQuestionById}
        paginatedQuestionsListManager={paginatedQuestionsListManager}
      />
    )
  };

  const questionSetProps = {
    selectable: () => true,
    isSelected: isQuestionSetSelected,
    questionSetOnChangeHandler: makeQuestionSetOnChangeHandler,
    rearrangeable: isRearranging,
    viewingFromFolder: true,
    selectedQuestionSets,
    isSelectAllQuestionSetsMode
  };
  // ### END PROPS ###

  if (!paginatedQuestionsListManager) {
    return null;
  }

  return (
    <QuestionsList
      {...questionsListProps}
      className={classnames(
        'practice-view__questions-list u-display_flex u-flex-direction_column',
        {
          'practice-view__questions-list--in-view': isQuestionsListInView
        }
      )}
      activeQuestion={activeQuestion}
      questionSetProps={{
        ...questionSetProps,
        hasQuestionSetAccessPredicate: () => true
      }}
      questionProps={{
        showDifficulty: true,
        onQuestionClick: (args) => makeMobileOnQuestionClick(args)
      }}
      questionSets={questionSets}
      isLoading={isLoading}
    />
  );
};

export default FolderPracticeViewQuestionList;

interface HeaderProps extends WithRouterProps {
  selectedQuestionCount: number;
  toggleSelectAllQuestionSets: (e) => void;
  isRearranging: boolean;
  questionSetCount: number;
  isSelectAllQuestionSetsMode: boolean;
}

const TeacherAssignmentQuestionsListHeader = withRouter(
  ({
    selectedQuestionCount,
    toggleSelectAllQuestionSets,
    isRearranging,
    isSelectAllQuestionSetsMode,
    params
  }: HeaderProps) => {
    const {assignmentId} = params;
    const questionSetMeta = getQuestionSetsQuery(assignmentId).getResourceMetadata();
    const questionCount = questionSetMeta.getIn(['request', 'count_of_questions']);
    return (
      <QuestionsListHeader
        heading={`${questionCount} ${pluralize('Question', questionCount)}`}
        checkboxProps={
          !isRearranging
            ? {
                checked: isSelectAllQuestionSetsMode || selectedQuestionCount === questionCount,
                'aria-hidden': false,
                onChange: toggleSelectAllQuestionSets,
                indeterminate: selectedQuestionCount > 0 && selectedQuestionCount < questionCount
              }
            : undefined
        }
        selectedQuestionCount={selectedQuestionCount}
      />
    );
  }
);
